import { useState, useMemo } from "react";
import { useAdmin } from "../admin-context";
import { BarChart3, TrendingUp, ShoppingCart, Users, DollarSign, ArrowUpRight, ArrowDownRight, Download } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";

const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const PIE_COLORS   = ["#f97316","#fb923c","#fdba74","#fed7aa","#ffedd5","#fcd34d","#86efac","#67e8f9"];

const colorMap = {
  orange: { bg: "bg-orange-100", text: "text-orange-600", icon: "bg-orange-500" },
  blue:   { bg: "bg-blue-100",   text: "text-blue-600",   icon: "bg-blue-500" },
  green:  { bg: "bg-green-100",  text: "text-green-600",  icon: "bg-green-500" },
  purple: { bg: "bg-purple-100", text: "text-purple-600", icon: "bg-purple-500" },
};

const getAmount = (o) =>
  Number(o.amount) ||
  (o.items || []).reduce((s, i) => s + Number(i.price) * Number(i.quantity || 1), 0);

const Reports = () => {
  const { orders, products, users } = useAdmin();
  const [range, setRange] = useState("year");

  // ── Monthly data aggregated from real orders ─────────────────
  const allMonthlyData = useMemo(() => {
    const map = {};
    orders.forEach(o => {
      if (!o.date) return;
      const d   = new Date(o.date);
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
      if (!map[key]) map[key] = {
        month:  MONTHS_SHORT[d.getMonth()],
        _sort:  d.getFullYear() * 100 + d.getMonth(),
        orders:  0,
        revenue: 0,
      };
      map[key].orders++;
      map[key].revenue += getAmount(o);
    });
    return Object.values(map).sort((a, b) => a._sort - b._sort);
  }, [orders]);

  const monthlySales = useMemo(() => {
    if (range === "week")  return allMonthlyData.slice(-4);
    if (range === "month") return allMonthlyData.slice(-6);
    return allMonthlyData;
  }, [allMonthlyData, range]);

  // ── Summary stats ────────────────────────────────────────────
  const totalRevenue   = useMemo(() => orders.reduce((s, o) => s + getAmount(o), 0), [orders]);
  const totalOrders    = orders.length;
  const avgOrderValue  = totalOrders > 0 ? totalRevenue / totalOrders : 0;

  // Month-over-month change helpers
  const pctChange = useMemo(() => {
    const len = allMonthlyData.length;
    if (len < 2) return { revenue: null, orders: null };
    const cur  = allMonthlyData[len - 1];
    const prev = allMonthlyData[len - 2];
    const rev  = prev.revenue ? Math.round(((cur.revenue - prev.revenue) / prev.revenue) * 100) : null;
    const ord  = prev.orders  ? Math.round(((cur.orders  - prev.orders)  / prev.orders)  * 100) : null;
    return { revenue: rev, orders: ord };
  }, [allMonthlyData]);

  const statCards = [
    {
      label: "Total Revenue",
      value: `$${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
      change: pctChange.revenue,
      icon: DollarSign,
      color: "orange",
    },
    {
      label: "Total Orders",
      value: totalOrders.toLocaleString(),
      change: pctChange.orders,
      icon: ShoppingCart,
      color: "blue",
    },
    {
      label: "Total Customers",
      value: users.length.toLocaleString(),
      change: null,
      icon: Users,
      color: "green",
    },
    {
      label: "Avg Order Value",
      value: `$${avgOrderValue.toFixed(2)}`,
      change: null,
      icon: TrendingUp,
      color: "purple",
    },
  ];

  // ── Category pie — products count per category ────────────────
  const categoryData = useMemo(() => {
    const map = {};
    products.forEach(p => {
      const cat = p.category?.name || "Uncategorized";
      map[cat] = (map[cat] || 0) + 1;
    });
    const total = Object.values(map).reduce((s, v) => s + v, 0) || 1;
    return Object.entries(map).map(([name, count], i) => ({
      name,
      value: Math.round((count / total) * 100),
      color: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [products]);

  // ── Top products aggregated from order items ──────────────────
  const topProducts = useMemo(() => {
    const map = {};
    // build product→category lookup by name
    const catByName = {};
    products.forEach(p => { catByName[p.name] = p.category?.name || "—"; });

    orders.forEach(o => {
      (o.items || []).forEach(item => {
        const name = item.name || item.title || "Unknown";
        if (!map[name]) map[name] = { name, orders: 0, revenue: 0, category: catByName[name] || "—" };
        map[name].orders  += Number(item.quantity) || 1;
        map[name].revenue += Number(item.price) * (Number(item.quantity) || 1);
      });
    });

    return Object.values(map)
      .sort((a, b) => b.orders - a.orders)
      .slice(0, 5)
      .map((p, i) => ({ ...p, rank: i + 1 }));
  }, [orders, products]);

  const isEmpty = orders.length === 0;

  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-500 text-sm">Track your store performance and key metrics.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            {["week", "month", "year"].map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-4 py-1.5 rounded-lg text-sm font-bold capitalize transition ${
                  range === r ? "bg-white shadow text-orange-600" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl font-bold shadow-lg shadow-orange-200 transition active:scale-95">
            <Download size={16} /> Export
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {statCards.map((card) => {
          const c  = colorMap[card.color];
          const up = card.change >= 0;
          return (
            <div key={card.label} className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-2.5 rounded-xl ${c.icon}`}>
                  <card.icon size={20} className="text-white" />
                </div>
                {card.change !== null ? (
                  <span className={`flex items-center gap-1 text-xs font-bold ${up ? "text-green-600" : "text-red-500"}`}>
                    {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                    {Math.abs(card.change)}%
                  </span>
                ) : (
                  <span className="text-xs text-gray-400 font-medium">All time</span>
                )}
              </div>
              <p className="text-2xl font-extrabold text-gray-900">{card.value}</p>
              <p className="text-sm text-gray-500 mt-1">{card.label}</p>
            </div>
          );
        })}
      </div>

      {/* Revenue Chart */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h3 className="font-bold text-lg text-gray-800 mb-6">Revenue Overview</h3>
        {isEmpty ? (
          <p className="text-center text-gray-400 py-16 text-sm">No order data yet.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={monthlySales}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#f97316" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#9ca3af" }} />
              <YAxis tick={{ fontSize: 12, fill: "#9ca3af" }} tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(0)+"k" : v}`} />
              <Tooltip
                contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,.1)" }}
                formatter={(v) => [`$${v.toLocaleString(undefined, {maximumFractionDigits:0})}`, "Revenue"]}
              />
              <Area type="monotone" dataKey="revenue" stroke="#f97316" strokeWidth={2.5} fill="url(#revenueGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Orders Bar Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="font-bold text-lg text-gray-800 mb-6">Orders Per Month</h3>
          {isEmpty ? (
            <p className="text-center text-gray-400 py-14 text-sm">No order data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={monthlySales}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#9ca3af" }} />
                <YAxis tick={{ fontSize: 12, fill: "#9ca3af" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,.1)" }}
                />
                <Bar dataKey="orders" fill="#f97316" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Category Pie */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h3 className="font-bold text-lg text-gray-800 mb-4">Products by Category</h3>
          {categoryData.length === 0 ? (
            <p className="text-center text-gray-400 py-14 text-sm">No products yet.</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={categoryData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                    {categoryData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => [`${v}%`, "Share"]} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 space-y-1.5">
                {categoryData.map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
                      <span className="text-gray-600">{item.name}</span>
                    </div>
                    <span className="font-bold text-gray-800">{item.value}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Top Products Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <h3 className="font-bold text-lg text-gray-800">Top Selling Products</h3>
        </div>
        {topProducts.length === 0 ? (
          <p className="text-center text-gray-400 py-10 text-sm">No order items yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left whitespace-nowrap">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold tracking-wider">
                <tr>
                  <th className="p-5">Rank</th>
                  <th className="p-5">Product</th>
                  <th className="p-5">Category</th>
                  <th className="p-5">Orders</th>
                  <th className="p-5">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {topProducts.map((p) => (
                  <tr key={p.rank} className="hover:bg-gray-50 transition">
                    <td className="p-5">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-extrabold ${
                        p.rank === 1 ? "bg-orange-100 text-orange-600" : "bg-gray-100 text-gray-500"
                      }`}>{p.rank}</span>
                    </td>
                    <td className="p-5 font-bold text-gray-900">{p.name}</td>
                    <td className="p-5 text-gray-500">{p.category}</td>
                    <td className="p-5 font-bold text-gray-800">{p.orders.toLocaleString()}</td>
                    <td className="p-5 font-bold text-gray-800">${p.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  );
};

export default Reports;
